import { Button, Flex, InputBase, Stack, Text } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import type { JSX } from "react";
import { IMaskInput } from "react-imask";
import { withPolymorphicClickAnalytics } from "@/analytics";
import classes from "./CustomerVerificationUpdated.module.css";

export function BillingAccountNumberAndPinSection({
    form,
    isEditing,
    onEditClick,
    onSaveClick,
}: {
    form: UseFormReturnType<
        {
            billingAccountNumber: string;
            pin: string;
        },
        (values: { billingAccountNumber: string; pin: string }) => {
            billingAccountNumber: string;
            pin: string;
        }
    >;
    isEditing: boolean;
    onEditClick: () => void;
    onSaveClick: () => void;
}): JSX.Element {
    const ButtonWithAnalytics = withPolymorphicClickAnalytics(Button<"button">, "Click");
    const isBillingAccountNumberAndPinValid = () => form.isValid("billingAccountNumber") && form.isValid("pin");

    return (
        <Flex direction="column">
            {isEditing ? (
                <Stack gap={0} w="100%">
                    <InputBase
                        data-testid="billing-account-number-input"
                        classNames={{
                            input: `${classes.styledInput} fs-mask`,
                            label: classes.styledLabel,
                        }}
                        mt="xxl"
                        size="sm"
                        c="dark.1"
                        label="What’s their account number?"
                        component={IMaskInput}
                        mask="*************"
                        key={form.key("billingAccountNumber")}
                        {...form.getInputProps("billingAccountNumber")}
                        // The below code is to fix the issue with the IMaskInput using the onAccept event rather than onChange. This was causing validation to not trigger in certain scenarios.
                        onChange={undefined}
                        onAccept={(value) => form.setFieldValue("billingAccountNumber", value)}
                    />
                    <Stack gap={0} w="100%">
                        <InputBase
                            data-testid="pin-input"
                            classNames={{
                                input: `${classes.styledInput} fs-mask`,
                                label: classes.styledLabel,
                            }}
                            mt="xxl"
                            size="sm"
                            c="dark.1"
                            label="What’s their PIN? (Optional)"
                            component={IMaskInput}
                            mask={Number}
                            key={form.key("pin")}
                            {...form.getInputProps("pin")}
                            // The below code is to fix the issue with the IMaskInput using the onAccept event rather than onChange. This was causing validation to not trigger in certain scenarios.
                            onChange={undefined}
                            onAccept={(value) => form.setFieldValue("pin", value)}
                        />
                        <Text c="dark.2" size="xxs" mt="3xs" mb="xxs">
                            Some accounts don’t use a PIN
                        </Text>
                    </Stack>
                    <ButtonWithAnalytics
                        size="sm"
                        fz="xs"
                        mt="sm"
                        disabled={!isBillingAccountNumberAndPinValid()}
                        onClick={onSaveClick}
                        analytics={{ actionId: "SaveBillingAccountNumberAndPin" }}
                    >
                        Save
                    </ButtonWithAnalytics>
                </Stack>
            ) : (
                <Flex direction="column">
                    <Flex align="center" justify="space-between">
                        <Text c="dark.1" size="sm" mb="md" mt="md">
                            What’s their account number?
                        </Text>
                        <ButtonWithAnalytics
                            td="underline"
                            variant="subtle"
                            color="dark.1"
                            size="sm"
                            fz="xs"
                            onClick={onEditClick}
                            analytics={{ actionId: "change billing account number and pin" }}
                        >
                            Change
                        </ButtonWithAnalytics>
                    </Flex>
                    <Text size="sm" c="dark.1" className="fs-mask">
                        {form.getValues().billingAccountNumber}
                    </Text>
                    {form.getValues().pin.length > 0 && (
                        <>
                            <Text c="dark.1" size="sm" mb="md" mt="md">
                                What’s their PIN? (Optional)
                            </Text>
                            <Text size="sm" c="dark.1" className="fs-mask">
                                {form.getValues().pin}
                            </Text>
                        </>
                    )}
                </Flex>
            )}
        </Flex>
    );
}
